import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, RailzTaxRate, formattedCurrency, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import styles from './BillBalances.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const classes = withStyles(styles);

interface TaxItems {
  amount: number;
  salesTaxId?: string;
}

interface BillBalancesProps {
  salesTax: TaxItems[];
  taxCalculation: 'inclusive' | 'exclusive' | 'outOfScope';
  taxRates: RailzTaxRate[];
  textArea: ReactNode;
}

const BillBalances: FC<BillBalancesProps> = ({ salesTax, taxCalculation, taxRates, textArea }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Software.Common.Balances' });
  const {
    response: { Country },
  } = useClientData();
  const currency = useMemo(() => {
    if (Country === 'US') {
      return 'USD';
    }
    return 'CAD';
  }, [Country]);
  const [subTotal, setSubTotal] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [taxes, setTaxes] = useState<
    {
      taxName: string;
      rate: number;
      amount: number;
      value: number;
    }[]
  >([]);
  useEffect(() => {
    // Calculate subtotal (always sum of amounts)
    const calculatedSubTotal = salesTax.reduce((acc, item) => acc + item.amount, 0);
    setSubTotal(calculatedSubTotal);

    // Initialize tax calculation map
    const taxesMap: Record<
      string,
      {
        rate: number;
        value: number;
        totalAmount: number;
      }
    > = {};

    // Calculate taxes based on type
    salesTax.forEach((item) => {
      if (!item.salesTaxId) return;
      const taxRateObj = taxRates.find((rate) => rate.id === item.salesTaxId);
      if (!taxRateObj) return;

      const rate = (taxRateObj.totalTaxRate || 0) / 100; // Assuming 0.05 for 5%, etc.
      let taxAmount = 0;
      let totalAmount = 0;

      if (taxCalculation === 'inclusive') {
        // Tax is included in the amount, extract it
        taxAmount = item.amount - item.amount / (1 + rate);
        totalAmount = item.amount - taxAmount;
      } else if (taxCalculation === 'exclusive') {
        // Tax is on top of the amount
        taxAmount = item.amount * rate;
        totalAmount = item.amount;
      } else if (taxCalculation === 'outOfScope') {
        taxAmount = 0;
        totalAmount = item.amount;
      }

      if (!taxesMap[item.salesTaxId]) {
        taxesMap[item.salesTaxId] = { rate, value: 0, totalAmount: 0 };
      }

      taxesMap[item.salesTaxId].value += taxAmount;
      taxesMap[item.salesTaxId].totalAmount += totalAmount;
    });

    // Prepare taxes array for display
    const taxesArray = Object.keys(taxesMap).map((salesTaxId) => {
      const { rate, value, totalAmount } = taxesMap[salesTaxId];
      return {
        taxName: taxRates.find((r) => r.id === salesTaxId)?.name || '',
        rate,
        amount: totalAmount,
        value,
      };
    });

    setTaxes(taxesArray);

    // Calculate total
    const totalTaxes = taxesArray.reduce((acc, t) => acc + t.value, 0);

    let calculatedTotal = 0;

    if (taxCalculation === 'inclusive' || taxCalculation === 'outOfScope') {
      calculatedTotal = calculatedSubTotal;
    } else if (taxCalculation === 'exclusive') {
      calculatedTotal = calculatedSubTotal + totalTaxes;
    }

    if (taxCalculation === 'inclusive') {
      setSubTotal(calculatedTotal - totalTaxes);
    }

    setTotal(calculatedTotal);
  }, [salesTax, taxCalculation, taxRates]);

  return (
    <Box jc="sb" ai="fs" base="8">
      <div className={classes('textAreaContainer')}>{textArea}</div>
      <div className={classes('invoiceBalances')}>
        <div className={classes('invoiceBalancesRow')}>
          <div>{t('total', { defaultValue: 'Total' })}</div>
          <div>
            {currency} {formattedCurrency(subTotal.toString())}
          </div>
        </div>
        {taxes.map((tax) => (
          <div key={tax.rate} className={classes('invoiceBalancesRow')}>
            <div>{`${tax.taxName} ${tax.rate * 100}% @ ${currency} ${formattedCurrency(tax.amount.toString())}`}</div>
            <div>
              {currency} {formattedCurrency(tax.value.toString())}
            </div>
          </div>
        ))}
        <div className={classes('dashed')} />
        <div className={classes('invoiceBalancesRow', 'invoiceBalancesDue')}>
          <div>{t('balanceDue', { defaultValue: 'Balance Due' })}</div>
          <div>
            {currency} {formattedCurrency(total.toString())}
          </div>
        </div>
      </div>
    </Box>
  );
};

export default BillBalances;
